import { useEffect, useRef, useState } from 'react'
import { forRecording, releaseRecording } from '../lib/audio-session.ts'

/**
 * ЗАПИСЬ ГОЛОСОВОГО
 *
 * Не «зажми и держи», а обычное нажатие: началась запись — видно таймер
 * и звуковую дорожку, рядом «отменить» и «отправить». Удержание удобно
 * на телефоне и мучительно на компьютере, а приложение одно на всех.
 *
 * Формат выбирает браузер: Chrome пишет webm, Safari — mp4. Не навязываем
 * свой: перекодирование на слабом сервере съест процессор ради копеечного
 * выигрыша в размере.
 */

const MAX_SECONDS = 300
/** Столько столбиков в дорожке: больше на узком экране не различить. */
const BARS = 48

type Props = {
  onSend: (file: File, durationSec: number, waveform: number[]) => void
  onCancel: () => void
}

/**
 * Safari — iPhone, iPad и Mac. Chrome и Edge тоже называют себя «Safari»,
 * поэтому отсекаем их по собственным меткам.
 */
const isSafari = (): boolean =>
  /Safari/.test(navigator.userAgent) && !/Chrome|Chromium|CriOS|Edg|FxiOS|Android/.test(navigator.userAgent)

function pickMimeType(): string {
  /**
   * На iPhone — только mp4.
   *
   * Новые iOS научились писать и webm, и приложение стало выбирать его.
   * Но webm iPhone пишет потоком, без длительности, и потом сам же не
   * умеет такой файл перематывать: голосовое звучало один раз, а дальше
   * молчало. Mp4 для Safari родной — с длительностью и перемоткой.
   */
  const candidates = isSafari()
    ? ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm']
    : ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus']
  for (const type of candidates) {
    if (MediaRecorder.isTypeSupported(type)) return type
  }
  return ''
}

export function VoiceRecorder({ onSend, onCancel }: Props) {
  const [seconds, setSeconds] = useState(0)
  const [levels, setLevels] = useState<number[]>([])
  const [error, setError] = useState<string | null>(null)

  const recorder = useRef<MediaRecorder | null>(null)
  const chunks = useRef<Blob[]>([])
  const stream = useRef<MediaStream | null>(null)
  const audioContext = useRef<AudioContext | null>(null)
  const frame = useRef<number | undefined>(undefined)
  const collected = useRef<number[]>([])
  const startedAt = useRef(0)
  const finishing = useRef(false)

  /** Останавливает всё: дорожку микрофона, разбор звука, таймер. */
  const teardown = () => {
    if (frame.current !== undefined) cancelAnimationFrame(frame.current)
    // Дорожку обязательно закрываем: иначе в браузере остаётся гореть
    // значок «идёт запись», даже когда мы уже всё отправили
    stream.current?.getTracks().forEach((track) => track.stop())
    releaseRecording()
    void audioContext.current?.close().catch(() => {})
    stream.current = null
    audioContext.current = null
  }

  useEffect(() => {
    let alive = true

    const start = async () => {
      try {
        forRecording()
        const media = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true },
        })
        if (!alive) {
          media.getTracks().forEach((t) => t.stop())
          return
        }

        stream.current = media

        const context = new AudioContext()
        audioContext.current = context
        const analyser = context.createAnalyser()
        analyser.fftSize = 512
        context.createMediaStreamSource(media).connect(analyser)

        const data = new Uint8Array(analyser.frequencyBinCount)

        /** Громкость сейчас — от неё растут столбики дорожки. */
        const sample = () => {
          analyser.getByteTimeDomainData(data)
          let peak = 0
          for (const value of data) peak = Math.max(peak, Math.abs(value - 128))
          // 128 — полная шкала; умножаем, чтобы обычная речь
          // не выглядела бы едва заметной рябью
          const level = Math.min(100, Math.round((peak / 128) * 220))

          collected.current.push(level)
          setLevels((current) => [...current.slice(-(BARS - 1)), level])

          frame.current = requestAnimationFrame(sample)
        }
        sample()

        const mimeType = pickMimeType()
        const rec = new MediaRecorder(media, mimeType ? { mimeType } : undefined)
        recorder.current = rec
        chunks.current = []

        rec.ondataavailable = (event) => {
          if (event.data.size > 0) chunks.current.push(event.data)
        }

        rec.onstop = () => {
          teardown()
          if (!finishing.current) return

          const blob = new Blob(chunks.current, { type: rec.mimeType || 'audio/webm' })
          const extension = (rec.mimeType || '').includes('mp4') ? 'm4a' : 'webm'
          const duration = Math.max(1, Math.round((Date.now() - startedAt.current) / 1000))

          // Сжимаем собранные замеры до числа столбиков: их набирается
          // по шестьдесят в секунду, а нужно несколько десятков на всё
          const raw = collected.current
          const step = Math.max(1, Math.floor(raw.length / BARS))
          const waveform: number[] = []
          for (let i = 0; i < raw.length; i += step) {
            const slice = raw.slice(i, i + step)
            waveform.push(Math.round(slice.reduce((a, b) => a + b, 0) / slice.length))
          }

          onSend(
            new File([blob], `голосовое.${extension}`, { type: blob.type }),
            duration,
            waveform.slice(0, BARS),
          )
        }

        startedAt.current = Date.now()
        rec.start()
      } catch {
        // Отказ в доступе к микрофону — самая частая причина
        setError('Нет доступа к микрофону. Разреши его в настройках браузера.')
      }
    }

    void start()

    return () => {
      alive = false
      finishing.current = false
      if (recorder.current?.state === 'recording') recorder.current.stop()
      teardown()
    }
  }, [])

  // Таймер и предел длины
  useEffect(() => {
    if (error) return
    const timer = window.setInterval(() => {
      setSeconds((value) => {
        if (value + 1 >= MAX_SECONDS) {
          finish()
          return MAX_SECONDS
        }
        return value + 1
      })
    }, 1000)
    return () => window.clearInterval(timer)
  }, [error])

  const finish = () => {
    finishing.current = true
    if (recorder.current?.state === 'recording') recorder.current.stop()
    else onCancel()
  }

  const drop = () => {
    finishing.current = false
    if (recorder.current?.state === 'recording') recorder.current.stop()
    teardown()
    onCancel()
  }

  if (error) {
    return (
      <div className="recorder">
        <div className="recorder-error">{error}</div>
        <button className="btn-quiet" onClick={onCancel}>
          Закрыть
        </button>
      </div>
    )
  }

  const time = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`

  return (
    <div className="recorder">
      <button className="recorder-drop" onClick={drop} title="Отменить" aria-label="Отменить">
        🗑
      </button>

      <span className="recorder-dot" aria-hidden="true" />
      <span className="recorder-time">{time}</span>

      <div className="recorder-wave" aria-hidden="true">
        {levels.map((level, index) => (
          <span key={index} style={{ height: `${Math.max(3, level)}%` }} />
        ))}
      </div>

      <button className="recorder-send" onClick={finish} title="Отправить" aria-label="Отправить">
        <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
          <path d="M2.01 21 23 12 2.01 3 2 10l15 2-15 2z" />
        </svg>
      </button>
    </div>
  )
}
